import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import icon from 'flarum/common/helpers/icon';
import IndexPage from 'flarum/forum/components/IndexPage';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import Navigation from 'flarum/common/components/Navigation';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import LogInModal from 'flarum/forum/components/LogInModal';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostUser from 'flarum/forum/components/PostUser';
import UserCard from 'flarum/forum/components/UserCard';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import Search from 'flarum/forum/components/Search';
import Stream from 'flarum/common/utils/Stream';
import extractText from 'flarum/common/utils/extractText';
import TagDiscussionModal from 'flarum/tags/forum/components/TagDiscussionModal';
import TagsPage from 'flarum/tags/forum/components/TagsPage';
import Tag from 'flarum/tags/common/models/Tag';

import HomeDashboard from './components/HomeDashboard';
import ModerationDecisionModal from './components/ModerationDecisionModal';
import ListingStatusModal from './components/ListingStatusModal';
import { Pakt21LogoMark } from './components/Pakt21Logo';
import Pakt21IdPage from './components/Pakt21IdPage';
import Pakt21InfoPage from './components/Pakt21InfoPage';
import ProfileDetailsModal from './components/ProfileDetailsModal';
import Pakt21IdSearchSource from './Pakt21IdSearchSource';
import { tagFallbackImageUrl } from './tagImage';
import pakt21ApiUrl from '../common/pakt21ApiUrl';
import './adminUserAttributes';

const THEME_STORAGE_KEY = 'pakt21-theme';
// Logo przy nicku pozostaje wyłączone do chwili ustalenia progu przez właściciela PAKT21.
const OFFICIAL_MARK_TRUST_LEVEL = null;
const ANONYMOUS_TOPIC_TRUST_LEVEL = 3;
const ANONYMOUS_TOPIC_TAG = 'potrzebuje-pomocy';
const FEATURED_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
const FEATURED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);
const LEGACY_FORUM_TAGS = new Set(['general', 'pomoc', 'oferta', 'spolecznosc', 'arena', 'dzialamy', 'podyskutujmy']);
const RETIRED_MAIN_CATEGORIES = new Set(['sprawdzmy-to', 'sprawy-do-wyjasnienia']);
const LISTING_TYPES = new Set(['kupie', 'sprzedam', 'oferuje', 'poszukuje']);
const MARKET_CATEGORIES = [
  { slug: 'oferuje', translationKey: 'offer_service', icon: 'fas fa-hand-holding-heart' },
  { slug: 'poszukuje', translationKey: 'find_contractor', icon: 'fas fa-tools' },
  { slug: 'szukam-pracy', translationKey: 'find_work', icon: 'fas fa-user-tie' },
  { slug: 'szukam-pracownika', translationKey: 'find_worker', icon: 'fas fa-user-plus' },
  { slug: 'kupie', translationKey: 'buy', icon: 'fas fa-shopping-basket' },
  { slug: 'sprzedam', translationKey: 'sell', icon: 'fas fa-tag' },
  { slug: 'szukam-wspolnika', translationKey: 'find_partner', icon: 'fas fa-handshake' },
  { slug: 'szukam-inwestora', translationKey: 'find_investor', icon: 'fas fa-chart-line' },
  { slug: 'mam-pomysl', translationKey: 'project_idea', icon: 'far fa-lightbulb' },
];
const SHARED_CAUSE_CATEGORIES = [
  {
    slug: 'wielki-atlas-wybrancow-narodu',
    translationKey: 'public_officials_atlas',
    icon: 'fas fa-landmark',
  },
  {
    slug: 'niewygodne',
    translationKey: 'uncomfortable',
    icon: 'fas fa-bullhorn',
  },
  {
    slug: 'potrzebuje-pomocy',
    translationKey: 'need_help',
    icon: 'far fa-life-ring',
  },
];
const COMMUNITY_CATEGORIES = [
  {
    slug: 'budujemy-pakt21',
    translationKey: 'build_pakt21',
    icon: 'fas fa-tools',
  },
  {
    slug: 'nasze-hobby',
    translationKey: 'hobbies',
    icon: 'fas fa-compass',
  },
];
const ARENA_CATEGORIES = [
  {
    slug: 'arena-p21',
    translationKey: 'arena_p21',
    icon: 'fas fa-balance-scale',
  },
];
const CATEGORY_TRANSLATION_KEYS = new Map([
  ...MARKET_CATEGORIES,
  ...SHARED_CAUSE_CATEGORIES,
  ...COMMUNITY_CATEGORIES,
  ...ARENA_CATEGORIES,
  { slug: 'ogloszenia-administracji', translationKey: 'announcements' },
].map(({ slug, translationKey }) => [slug, translationKey]));

function trans(key, input = {}) {
  return app.translator.trans(`pakt21-beta.forum.navigation.${key}`, input);
}

function categoryTrans(key) {
  return app.translator.trans(`pakt21-beta.forum.category_selection.${key}`);
}

function navigationTrans(key) {
  return app.translator.trans(`pakt21-beta.forum.navigation.${key}`);
}

function categoryUiTrans(translationKey, field) {
  return app.translator.trans(`pakt21-beta.forum.static_categories.${translationKey}.${field}`);
}

function categoryUiText(translationKey, field) {
  return extractText(categoryUiTrans(translationKey, field));
}

function featuredImageTrans(key) {
  return app.translator.trans(`pakt21-beta.forum.featured_image.${key}`);
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;

  document.documentElement.setAttribute('data-pakt21-theme', theme);
}

function initializeTheme() {
  if (typeof window === 'undefined') return;

  const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
  // Każde urządzenie zaczyna od tego samego, zatwierdzonego jasnego wyglądu.
  // Ciemny motyw pozostaje świadomym wyborem zapisywanym w przeglądarce.
  applyTheme(saved === 'dark' || saved === 'light' ? saved : 'light');
}

function memberMark(user, className = '') {
  const memberNumber = user.attribute('pakt21MemberNumber');
  const trustLevel = Number(user.attribute('pakt21TrustLevel') || 0);
  const showOfficialMark = Number.isInteger(OFFICIAL_MARK_TRUST_LEVEL)
    && trustLevel >= OFFICIAL_MARK_TRUST_LEVEL;

  if (memberNumber == null) {
    return null;
  }

  return (
    <Link
      className={`Pakt21MemberMark ${className}`.trim()}
      href={`/id/${memberNumber}`}
      title={app.translator.trans('pakt21-beta.forum.user.member_number_title', {
        number: memberNumber,
      })}
    >
      {showOfficialMark ? Pakt21LogoMark({ className: 'Pakt21OfficialMark' }) : null}
      <span className="Pakt21MemberMark-number">#{memberNumber}</span>
    </Link>
  );
}

function selectedAnonymousTopicTag(composer) {
  return (composer.composer.fields.tags || []).some((tag) => tag.slug() === ANONYMOUS_TOPIC_TAG);
}

function selectedListingType(composer) {
  return (composer.composer.fields.tags || []).map((tag) => tag.slug()).find((slug) => LISTING_TYPES.has(slug)) || null;
}

function selectedArenaTag(composer) {
  return (composer.composer.fields.tags || []).some((tag) => tag.slug() === 'arena-p21');
}

function listingTrans(key, input = {}) {
  return app.translator.trans(`pakt21-beta.forum.listing.${key}`, input);
}

function arenaTrans(key, input = {}) {
  return app.translator.trans(`pakt21-beta.forum.arena.${key}`, input);
}

function defaultArenaDateTime() {
  const date = new Date(Date.now() + (24 * 60 * 60 * 1000));
  const pad = (value) => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function arenaDateTime(value) {
  if (!value) return '';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(new Date(value));
}

function canPublishAnonymousTopic() {
  return Number(app.session.user?.attribute('pakt21TrustLevel') || 0) >= ANONYMOUS_TOPIC_TRUST_LEVEL;
}

function currentForumTag(tag) {
  return !LEGACY_FORUM_TAGS.has(tag.slug()) && !RETIRED_MAIN_CATEGORIES.has(tag.slug());
}

function startDiscussionFromMobile(event) {
  event?.preventDefault();

  if (!app.session.user) {
    app.modal.show(LogInModal);
    return;
  }

  app.composer.load(DiscussionComposer, { user: app.session.user });
  app.composer.show();
}

function mobileBottomNavigation() {
  const routeValue = typeof m.route?.get === 'function' ? m.route.get() : '/';
  const currentPath = String(routeValue || '/').split('?')[0];
  const profileUrl = app.session.user ? `/u/${app.session.user.slug()}` : null;
  const label = (key) => app.translator.trans(`pakt21-beta.forum.dashboard.mobile_nav_${key}`);

  return (
    <nav className="Pakt21MobileBottomNav" aria-label={label('label')}>
      <Link href="/" className={currentPath === '/' ? 'is-active' : ''}>
        {icon('fas fa-home')} <span>{label('home')}</span>
      </Link>
      <Link href="/tags" className={currentPath === '/tags' ? 'is-active' : ''}>
        {icon('fas fa-th-large')} <span>{label('spaces')}</span>
      </Link>
      <button type="button" className="Pakt21MobileBottomNav-create" onclick={startDiscussionFromMobile}>
        <span className="Pakt21MobileBottomNav-createIcon">{icon('fas fa-plus')}</span>
        <span>{label('create')}</span>
      </button>
      {profileUrl ? (
        <Link href={profileUrl} className={currentPath.startsWith('/u/') ? 'is-active' : ''}>
          {icon('fas fa-user')} <span>{label('profile')}</span>
        </Link>
      ) : (
        <button type="button" onclick={() => app.modal.show(LogInModal)}>
          {icon('fas fa-sign-in-alt')} <span>{label('login')}</span>
        </button>
      )}
    </nav>
  );
}

function changeLocale(event, locale) {
  event.preventDefault();

  if (locale === app.data?.locale) {
    return;
  }

  if (!app.session.user) {
    window.location.assign(localeSwitchUrl(locale));
    return;
  }

  app.session.user.savePreferences({ locale }).then(() => window.location.reload());
}

function localeSwitchUrl(locale) {
  const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  return `/pakt21/locale/${locale}?return=${encodeURIComponent(returnUrl)}`;
}

function mobileLanguageSwitcher() {
  const locales = app.data?.locales || {};
  const mobileLocales = ['pl', 'de'].filter((locale) => Object.prototype.hasOwnProperty.call(locales, locale));

  if (!app.forum.attribute('showLanguageSelector') || mobileLocales.length < 2) {
    return null;
  }

  return (
    <nav
      className="Pakt21MobileLanguage"
      aria-label={app.translator.trans('core.forum.header.locale_dropdown_accessible_label')}
    >
      <span className="sr-only">{app.translator.trans('core.forum.header.locale_dropdown_accessible_label')}</span>
      {mobileLocales.map((locale, index) => (
        <span>
          {index ? <span className="Pakt21MobileLanguage-separator" aria-hidden="true">/</span> : null}
          <a
            href={localeSwitchUrl(locale)}
            className={locale === (app.data?.locale || document.documentElement.lang) ? 'is-active' : ''}
            aria-current={locale === (app.data?.locale || document.documentElement.lang) ? 'true' : null}
            onclick={(event) => changeLocale(event, locale)}
          >
            {locale.toUpperCase()}
          </a>
        </span>
      ))}
    </nav>
  );
}

app.initializers.add('pakt21-beta', () => {
  app.translator.addTranslations({
    'flarum-tags.forum.composer_discussion.choose_tags_link': extractText(categoryTrans('choose_link')),
    'flarum-tags.forum.index.tags_link': extractText(categoryTrans('navigation')),
    'flarum-tags.forum.all_tags.meta_title_text': extractText(categoryTrans('navigation')),
    'flarum-tags.forum.choose_tags.title': extractText(categoryTrans('title')),
    'flarum-tags.forum.choose_tags.edit_title': extractText(categoryTrans('edit_title')),
    'flarum-tags.forum.discussion_controls.edit_tags_button': extractText(categoryTrans('edit_button')),
    'flarum-tags.lib.tag_selection_modal.title': extractText(categoryTrans('title')),
    'flarum-tags.lib.tag_selection_modal.choose_primary_placeholder': extractText(categoryTrans('instruction')),
  });

  app.routes['pakt21.user-id'] = { path: '/id/:number', component: Pakt21IdPage };
  app.routes['pakt21.manifest'] = {
    path: '/manifest',
    component: Pakt21InfoPage,
  };
  app.routes['pakt21.rules'] = {
    path: '/regulamin',
    component: Pakt21InfoPage,
  };

  if (Tag) {
    override(Tag.prototype, 'name', function (original) {
      const translationKey = CATEGORY_TRANSLATION_KEYS.get(this.slug());

      return translationKey ? categoryUiText(translationKey, 'name') : original();
    });

    override(Tag.prototype, 'description', function (original) {
      const translationKey = CATEGORY_TRANSLATION_KEYS.get(this.slug());

      return translationKey ? categoryUiText(translationKey, 'description') : original();
    });
  }

  extend(Search.prototype, 'sourceItems', function (items) {
    items.add('pakt21Id', new Pakt21IdSearchSource(), 200);
  });

  initializeTheme();

  extend(SignUpModal.prototype, 'oninit', function () {
    this.pakt21MinimumAgeDeclaration = Stream(false);
    this.pakt21RegistrationConsent = Stream(false);
  });

  extend(SignUpModal.prototype, 'fields', function (items) {
    items.add(
      'pakt21RegistrationConsent',
      <div className="Form-group Pakt21SignUp-registrationConsent">
        <label className="checkbox">
          <input
            type="checkbox"
            required
            checked={this.pakt21RegistrationConsent()}
            disabled={this.loading}
            onchange={(event) => this.pakt21RegistrationConsent(event.target.checked)}
          />
          <span>
            {app.translator.trans('pakt21-beta.forum.sign_up.consent_prefix')}{' '}
            <a href="/regulamin" target="_blank" rel="noopener">
              {app.translator.trans('pakt21-beta.forum.sign_up.rules_link')}
            </a>{' '}
            {app.translator.trans('pakt21-beta.forum.sign_up.consent_connector')}{' '}
            <a href="/regulamin#rules-privacy" target="_blank" rel="noopener">
              {app.translator.trans('pakt21-beta.forum.sign_up.privacy_link')}
            </a>.
          </span>
        </label>
        <p className="helpText">{app.translator.trans('pakt21-beta.forum.sign_up.consent_help')}</p>
      </div>,
      -1
    );

    items.add(
      'pakt21MinimumAgeDeclaration',
      <div className="Form-group Pakt21SignUp-minimumAgeDeclaration">
        <label className="checkbox">
          <input
            type="checkbox"
            required
            checked={this.pakt21MinimumAgeDeclaration()}
            disabled={this.loading}
            onchange={(event) => this.pakt21MinimumAgeDeclaration(event.target.checked)}
          />
          <span>{app.translator.trans('pakt21-beta.forum.sign_up.minimum_age_declaration')}</span>
        </label>
        <p className="helpText">
          {app.translator.trans('pakt21-beta.forum.sign_up.minimum_age_declaration_help')}
        </p>
      </div>,
      0
    );
  });

  extend(SignUpModal.prototype, 'submitData', function (data) {
    data.pakt21MinimumAgeDeclaration = this.pakt21MinimumAgeDeclaration();
    data.pakt21RegistrationConsent = this.pakt21RegistrationConsent();
  });

  if (DiscussionComposer) {
    extend(DiscussionComposer.prototype, 'oninit', function () {
      this.pakt21PublishAnonymously = false;
      this.pakt21ListingLocation = Stream('');
      this.pakt21ListingRemote = false;
      this.pakt21ListingArrangement = Stream('negotiable');
      this.pakt21ListingPrice = Stream('');
      this.pakt21ListingAvailability = Stream('');
      this.pakt21ArenaScope = Stream('user');
      this.pakt21ArenaTargetUsername = Stream('');
      this.pakt21ArenaScheduledAt = Stream(defaultArenaDateTime());
      this.pakt21FeaturedImage = null;
      this.pakt21FeaturedImageName = '';
      this.pakt21FeaturedImageError = '';
    });

    extend(DiscussionComposer.prototype, 'headerItems', function (items) {
      const chooseImage = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        if (!FEATURED_IMAGE_TYPES.has(file.type)) {
          this.pakt21FeaturedImageError = featuredImageTrans('invalid_type');
          m.redraw();
          return;
        }

        if (file.size > FEATURED_IMAGE_MAX_BYTES) {
          this.pakt21FeaturedImageError = featuredImageTrans('too_large');
          m.redraw();
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          this.pakt21FeaturedImage = typeof reader.result === 'string' ? reader.result : null;
          this.pakt21FeaturedImageName = file.name;
          this.pakt21FeaturedImageError = '';
          m.redraw();
        };
        reader.onerror = () => {
          this.pakt21FeaturedImageError = featuredImageTrans('read_error');
          m.redraw();
        };
        reader.readAsDataURL(file);
      };

      items.add(
        'pakt21FeaturedImage',
        <div className="Pakt21FeaturedImageComposer">
          <div className="Pakt21FeaturedImageComposer-controls">
            <label className="Button Button--link Pakt21FeaturedImageComposer-button">
              {icon('far fa-image')}
              <span>{this.pakt21FeaturedImage ? featuredImageTrans('change') : featuredImageTrans('add')}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                disabled={this.loading}
                onchange={chooseImage}
              />
            </label>
            {this.pakt21FeaturedImage ? (
              <button
                type="button"
                className="Button Button--link Pakt21FeaturedImageComposer-remove"
                disabled={this.loading}
                onclick={() => {
                  this.pakt21FeaturedImage = null;
                  this.pakt21FeaturedImageName = '';
                  this.pakt21FeaturedImageError = '';
                }}
              >
                {featuredImageTrans('remove')}
              </button>
            ) : null}
          </div>
          {this.pakt21FeaturedImage ? (
            <div className="Pakt21FeaturedImageComposer-preview">
              <img src={this.pakt21FeaturedImage} alt="" />
              <span>{this.pakt21FeaturedImageName}</span>
            </div>
          ) : (
            <small>{featuredImageTrans('help')}</small>
          )}
          {this.pakt21FeaturedImageError ? (
            <p className="Pakt21FeaturedImageComposer-error" role="alert">{this.pakt21FeaturedImageError}</p>
          ) : null}
        </div>,
        7
      );
    });

    extend(DiscussionComposer.prototype, 'headerItems', function (items) {
      const isHelpTopic = selectedAnonymousTopicTag(this);

      if (!isHelpTopic) {
        this.pakt21PublishAnonymously = false;
        return;
      }

      if (!canPublishAnonymousTopic()) {
        items.add(
          'pakt21AnonymousUnavailable',
          <p className="Pakt21AnonymousComposer-note">
            {app.translator.trans('pakt21-beta.forum.anonymous.minimum_level', {
              level: ANONYMOUS_TOPIC_TRUST_LEVEL,
            })}
          </p>,
          5
        );
        return;
      }

      items.add(
        'pakt21AnonymousTopic',
        <div className="Pakt21AnonymousComposer">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={this.pakt21PublishAnonymously}
              disabled={this.loading}
              onchange={(event) => {
                this.pakt21PublishAnonymously = event.target.checked;
              }}
            />
            <strong>{app.translator.trans('pakt21-beta.forum.anonymous.publish')}</strong>
          </label>
          <p>{app.translator.trans('pakt21-beta.forum.anonymous.explanation')}</p>
        </div>,
        5
      );
    });

    extend(DiscussionComposer.prototype, 'headerItems', function (items) {
      const type = selectedListingType(this);
      if (!type) return;

      items.add(
        'pakt21ListingFields',
        <div className="Pakt21ListingComposer">
          <div className="Pakt21ListingComposer-heading">
            <strong>{listingTrans(`type_${type}`)}</strong>
            <span>{listingTrans('form_help')}</span>
          </div>
          <div className="Pakt21ListingComposer-grid">
            <label>
              <span>{listingTrans('location')}</span>
              <input className="FormControl" maxlength="200" disabled={this.pakt21ListingRemote}
                placeholder={listingTrans('location_placeholder')} bidi={this.pakt21ListingLocation} />
            </label>
            <label className="Pakt21ListingComposer-check checkbox">
              <input type="checkbox" checked={this.pakt21ListingRemote} onchange={(event) => this.pakt21ListingRemote = event.target.checked} />
              <span>{listingTrans('remote')}</span>
            </label>
            <label>
              <span>{listingTrans('arrangement')}</span>
              <select className="FormControl" bidi={this.pakt21ListingArrangement}>
                {['paid', 'free', 'barter', 'negotiable'].map((value) => (
                  <option value={value}>{listingTrans(`arrangement_${value}`)}</option>
                ))}
              </select>
            </label>
            <label>
              <span>{listingTrans('price')}</span>
              <input className="FormControl" maxlength="100" placeholder={listingTrans('price_placeholder')} bidi={this.pakt21ListingPrice} />
            </label>
            <label className="Pakt21ListingComposer-wide">
              <span>{listingTrans('availability')}</span>
              <input className="FormControl" maxlength="200" placeholder={listingTrans('availability_placeholder')} bidi={this.pakt21ListingAvailability} />
            </label>
          </div>
          <p>{listingTrans('privacy_notice')}</p>
        </div>,
        8
      );
    });

    extend(DiscussionComposer.prototype, 'headerItems', function (items) {
      if (!selectedArenaTag(this)) return;

      items.add(
        'pakt21ArenaFields',
        <div className="Pakt21ArenaComposer">
          <div className="Pakt21ArenaComposer-heading">
            <strong>{arenaTrans('composer_title')}</strong>
            <span>{arenaTrans('composer_help')}</span>
          </div>
          <div className="Pakt21ArenaComposer-scope" role="group" aria-label={arenaTrans('scope_label')}>
            <label className="checkbox">
              <input type="radio" name="pakt21-arena-scope" value="user"
                checked={this.pakt21ArenaScope() === 'user'}
                onchange={() => this.pakt21ArenaScope('user')} />
              <span>{arenaTrans('scope_user')}</span>
            </label>
            <label className="checkbox">
              <input type="radio" name="pakt21-arena-scope" value="public"
                checked={this.pakt21ArenaScope() === 'public'}
                onchange={() => this.pakt21ArenaScope('public')} />
              <span>{arenaTrans('scope_public')}</span>
            </label>
          </div>
          <div className="Pakt21ArenaComposer-grid">
            {this.pakt21ArenaScope() === 'user' ? (
              <label>
                <span>{arenaTrans('target_username')}</span>
                <input className="FormControl" maxlength="100" required
                  placeholder={arenaTrans('target_placeholder')} bidi={this.pakt21ArenaTargetUsername} />
              </label>
            ) : null}
            <label>
              <span>{arenaTrans('scheduled_at')}</span>
              <input className="FormControl" type="datetime-local" required bidi={this.pakt21ArenaScheduledAt} />
            </label>
          </div>
          <p>{arenaTrans('acceptance_notice')}</p>
        </div>,
        9
      );
    });

    extend(DiscussionComposer.prototype, 'data', function (data) {
      if (this.pakt21FeaturedImage) {
        data.pakt21FeaturedImage = this.pakt21FeaturedImage;
      }

      data.pakt21IsAnonymous = Boolean(
        selectedAnonymousTopicTag(this)
        && canPublishAnonymousTopic()
        && this.pakt21PublishAnonymously
      );

      if (selectedListingType(this)) {
        data.pakt21ListingLocation = this.pakt21ListingLocation().trim();
        data.pakt21ListingRemote = Boolean(this.pakt21ListingRemote);
        data.pakt21ListingArrangement = this.pakt21ListingArrangement();
        data.pakt21ListingPrice = this.pakt21ListingPrice().trim();
        data.pakt21ListingAvailability = this.pakt21ListingAvailability().trim();
      }

      if (selectedArenaTag(this)) {
        data.pakt21ArenaScope = this.pakt21ArenaScope();
        data.pakt21ArenaTargetUsername = this.pakt21ArenaScope() === 'user'
          ? this.pakt21ArenaTargetUsername().trim()
          : '';
        const scheduledAt = this.pakt21ArenaScheduledAt();
        data.pakt21ArenaScheduledAt = scheduledAt ? new Date(scheduledAt).toISOString() : '';
      }
    });
  }

  extend(HeaderPrimary.prototype, 'items', function (items) {
    items.remove('title');
    items.add(
      'pakt21Brand',
      <Link
        className="Pakt21HeaderBrand"
        href="/"
        aria-label={app.translator.trans('pakt21-beta.forum.info_pages.navigation.home_label')}
      >
        <img src={pakt21ApiUrl(app.forum.attribute('apiUrl'), '/pakt21/assets/logo')} alt="PAKT21" />
      </Link>,
      100
    );
    items.add(
      'pakt21Manifest',
      <Link className="Pakt21HeaderInfoLink" href="/manifest">
        {app.translator.trans('pakt21-beta.forum.info_pages.navigation.manifest')}
      </Link>,
      90
    );
    items.add(
      'pakt21Rules',
      <Link className="Pakt21HeaderInfoLink" href="/regulamin">
        {app.translator.trans('pakt21-beta.forum.info_pages.navigation.rules')}
      </Link>,
      89
    );
    items.add(
      'pakt21MobileCategories',
      <Link className="Pakt21HeaderInfoLink Pakt21HeaderMobileCategories" href="/tags">
        {icon('fas fa-th-large')} {categoryTrans('open_categories')}
      </Link>,
      88
    );
  });

  extend(Navigation.prototype, 'view', function (vnode) {
    if (Array.isArray(vnode?.children)) {
      vnode.children.push(
        <nav
          className="Pakt21MobileHeaderLinks"
          aria-label={app.translator.trans('pakt21-beta.forum.dashboard.mobile_header_links')}
        >
          <Link className="Pakt21MobileHeaderManifest" href="/manifest">
            {app.translator.trans('pakt21-beta.forum.info_pages.navigation.manifest')}
          </Link>
          <Link
            className="Pakt21MobileHeaderSpaces"
            href="/tags"
            aria-label={app.translator.trans('pakt21-beta.forum.dashboard.mobile_header_spaces')}
          >
            {icon('fas fa-th-large')}
            <span>{app.translator.trans('pakt21-beta.forum.dashboard.mobile_header_spaces')}</span>
          </Link>
        </nav>
      );
      vnode.children.push(mobileLanguageSwitcher());
      vnode.children.push(mobileBottomNavigation());
    }
  });

  extend(IndexPage.prototype, 'sidebarItems', function (items) {
    items.add(
      'pakt21SidebarLogo',
      <Link className="Pakt21SidebarLogo" href="/" aria-label="PAKT21">
        <img src={pakt21ApiUrl(app.forum.attribute('apiUrl'), '/pakt21/assets/logo')} alt="PAKT21" />
      </Link>,
      200
    );
  });

  override(IndexPage.prototype, 'hero', function (original) {
    const path = typeof m.route?.get === 'function' ? m.route.get().split('?')[0] : '/';

    return path === '/' ? <HomeDashboard /> : original();
  });

  extend(IndexPage.prototype, 'navItems', function (items) {
    items.add('pakt21NavMain', <span className="Pakt21Nav-label">PAKT21</span>, 110);
    items.add(
      'pakt21Home',
      <Link href="/" className="Pakt21Nav-link">
        {icon('fas fa-home')} <span>{trans('home')}</span>
      </Link>,
      109
    );
    if (app.session.user) {
      items.add(
        'pakt21OwnTopics',
        <Link href={`/u/${app.session.user.slug()}/discussions`} className="Pakt21Nav-link">
          {icon('far fa-check-circle')} <span>{trans('my_topics')}</span>
        </Link>,
        106
      );
    }

    const availableTags = typeof app.store?.all === 'function' ? app.store.all('tags') : [];

    // Flarum dodaje te pozycje drugi raz. PAKT21 ma już ich jednoznaczne
    // odpowiedniki powyżej, dlatego usuwamy duplikaty i techniczny nagłówek.
    items.remove('allDiscussions');
    items.remove('following');
    items.remove('tags');

    availableTags
      .filter((tag) => !currentForumTag(tag))
      .forEach((tag) => items.remove(`tag${tag.id()}`));

    const announcementsTag = availableTags.find((tag) => tag.slug() === 'ogloszenia-administracji');

    if (announcementsTag) items.remove(`tag${announcementsTag.id()}`);

    MARKET_CATEGORIES.forEach(({ slug }) => {
      const loadedTag = availableTags.find((tag) => tag.slug() === slug);
      if (loadedTag) items.remove(`tag${loadedTag.id()}`);
    });

    SHARED_CAUSE_CATEGORIES.forEach(({ slug }) => {
      const loadedTag = availableTags.find((tag) => tag.slug() === slug);
      if (loadedTag) items.remove(`tag${loadedTag.id()}`);
    });

    COMMUNITY_CATEGORIES.forEach(({ slug }) => {
      const loadedTag = availableTags.find((tag) => tag.slug() === slug);
      if (loadedTag) items.remove(`tag${loadedTag.id()}`);
    });

    ARENA_CATEGORIES.forEach(({ slug }) => {
      const loadedTag = availableTags.find((tag) => tag.slug() === slug);
      if (loadedTag) items.remove(`tag${loadedTag.id()}`);
    });

    // Nie pokazujemy sztucznego odnośnika „Więcej”. Wszystkie właściwe
    // kategorie są widoczne bezpośrednio w nawigacji.
    items.remove('moreTags');

    items.add(
      'pakt21MarketLabel',
      <span className="Pakt21Nav-label Pakt21Nav-label--market">{navigationTrans('market')}</span>,
      4
    );

    MARKET_CATEGORIES.forEach(({ slug, translationKey, icon: categoryIcon }, index) => {
      items.add(
        `pakt21MarketCategory-${slug}`,
        <Link href={`/t/${slug}`} className="Pakt21Nav-link Pakt21Nav-link--market">
          {icon(categoryIcon)} <span>{categoryUiTrans(translationKey, 'name')}</span>
        </Link>,
        3.9 - (index / 10)
      );
    });

    items.add(
      'pakt21SharedCauseLabel',
      <span className="Pakt21Nav-label Pakt21Nav-label--sharedCause">{navigationTrans('shared_cause')}</span>,
      2.9
    );

    SHARED_CAUSE_CATEGORIES.forEach(({ slug, translationKey, icon: categoryIcon }, index) => {
      items.add(
        `pakt21SharedCauseCategory-${slug}`,
        <Link href={`/t/${slug}`} className="Pakt21Nav-link Pakt21Nav-link--sharedCause">
          {icon(categoryIcon)}
          <span className="Pakt21Nav-linkCopy">
            <strong>{categoryUiTrans(translationKey, 'name')}</strong>
            <small>{categoryUiTrans(translationKey, 'description')}</small>
          </span>
        </Link>,
        2.8 - (index / 10)
      );
    });

    items.add(
      'pakt21CommunityLabel',
      <span className="Pakt21Nav-label Pakt21Nav-label--community">{navigationTrans('community_space')}</span>,
      1.9
    );

    COMMUNITY_CATEGORIES.forEach(({ slug, translationKey, icon: categoryIcon }, index) => {
      items.add(
        `pakt21CommunityCategory-${slug}`,
        <Link href={`/t/${slug}`} className="Pakt21Nav-link Pakt21Nav-link--community">
          {icon(categoryIcon)}
          <span className="Pakt21Nav-linkCopy">
            <strong>{categoryUiTrans(translationKey, 'name')}</strong>
            <small>{categoryUiTrans(translationKey, 'description')}</small>
          </span>
        </Link>,
        1.8 - (index / 10)
      );
    });

    items.add(
      'pakt21ArenaLabel',
      <span className="Pakt21Nav-label Pakt21Nav-label--arena">{navigationTrans('arena')}</span>,
      0.9
    );

    ARENA_CATEGORIES.forEach(({ slug, translationKey, icon: categoryIcon }) => {
      items.add(
        `pakt21ArenaCategory-${slug}`,
        <Link href={`/t/${slug}`} className="Pakt21Nav-link Pakt21Nav-link--arena">
          {icon(categoryIcon)}
          <span className="Pakt21Nav-linkCopy">
            <strong>{categoryUiTrans(translationKey, 'name')}</strong>
            <small>{categoryUiTrans(translationKey, 'description')}</small>
          </span>
        </Link>,
        0.8
      );
    });

    if (announcementsTag) {
      items.add(
        'pakt21AnnouncementsLast',
        <Link href="/t/ogloszenia-administracji" className="Pakt21Nav-link Pakt21Nav-link--announcements">
          {icon('fas fa-bullhorn')} <span>{announcementsTag.name()}</span>
        </Link>,
        -30
      );
    }
  });

  if (TagDiscussionModal) {
    extend(TagDiscussionModal.prototype, 'oninit', function () {
      this.attrs.title = categoryTrans('title');
      this.attrs.allowResetting = false;
      this.attrs.limits.allowBypassing = false;
      this.attrs.limits.min.primary = 1;
      this.attrs.limits.max.primary = 1;
      this.attrs.limits.max.secondary = 1;
      this.bypassReqs = false;

      const originalFilter = this.attrs.selectableTags;
      const selected = this.attrs.discussion?.tags() || [];

      this.attrs.selectableTags = (tags) => {
        const selectable = originalFilter ? originalFilter(tags) : tags;

        return selectable.filter((tag) => currentForumTag(tag) || selected.includes(tag));
      };
    });

    override(DiscussionComposer.prototype, 'onsubmit', function (original) {
      const selectedTags = this.composer.fields.tags || [];
      const selectedPrimaryCategories = selectedTags.filter((tag) => tag.isPrimary());

      if (selectedPrimaryCategories.length !== 1) {
        app.modal.show(TagDiscussionModal, {
          selectedTags: selectedTags.slice(0),
          onsubmit: (tags) => {
            this.composer.fields.tags = tags;
            original();
          },
        });

        return;
      }

      original();
    });
  }

  if (TagsPage) {
    override(TagsPage.prototype, 'tagTileListView', function (original, tags) {
      return original(tags.filter(currentForumTag));
    });

    override(TagsPage.prototype, 'cloudView', function (original, tags) {
      return original(tags.filter(currentForumTag));
    });
  }

  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;
    const isVerified = Boolean(user.attribute('pakt21IsVerifiedAdult'));
    const trustLevel = Number(user.attribute('pakt21TrustLevel') || 0);
    const about = user.attribute('pakt21About');
    const location = user.attribute('pakt21Location');
    const contribution = user.attribute('pakt21Contribution');
    const needs = user.attribute('pakt21Needs');
    const skills = user.attribute('pakt21Skills') || [];
    const isProfileHero = String(this.attrs.className || '').includes('UserHero');
    const showProfileDetails = isProfileHero && !this.attrs.pakt21Compact;
    const profileMemberMark = isProfileHero ? memberMark(user, 'Pakt21MemberMark--profile') : null;

    if (profileMemberMark) {
      items.add('pakt21MemberNumber', profileMemberMark, 86);
    }

    items.add(
      'pakt21Verification',
      <span
        className={
          isVerified
            ? 'Pakt21UserStatus Pakt21UserStatus--verified'
            : 'Pakt21UserStatus Pakt21UserStatus--unverified'
        }
      >
        {icon(isVerified ? 'fas fa-check-circle' : 'far fa-circle')}{' '}
        {app.translator.trans(
          isVerified
            ? 'pakt21-beta.forum.user.status_verified'
            : 'pakt21-beta.forum.user.status_unverified'
        )}
      </span>,
      85
    );

    items.add(
      'pakt21TrustLevel',
      <span className="Pakt21UserTrustLevel">
        {app.translator.trans('pakt21-beta.forum.user.trust_level', {
          level: trustLevel,
        })}
      </span>,
      84
    );

    if (showProfileDetails && about) {
      items.add(
        'pakt21About',
        <section className="Pakt21ProfileField Pakt21ProfileField--about">
          <strong>{app.translator.trans('pakt21-beta.forum.profile.about_heading')}</strong>
          <span className="Pakt21UserAbout">{about}</span>
        </section>,
        70
      );
    }

    if (showProfileDetails && contribution) {
      items.add(
        'pakt21Contribution',
        <section className="Pakt21ProfileField">
          <strong>{app.translator.trans('pakt21-beta.forum.profile.contribution_heading')}</strong>
          <span>{contribution}</span>
        </section>,
        69
      );
    }

    if (showProfileDetails && needs) {
      items.add(
        'pakt21Needs',
        <section className="Pakt21ProfileField">
          <strong>{app.translator.trans('pakt21-beta.forum.profile.needs_heading')}</strong>
          <span>{needs}</span>
        </section>,
        68.5
      );
    }

    if (showProfileDetails && location) {
      items.add(
        'pakt21Location',
        <section className="Pakt21ProfileField Pakt21ProfileField--compact">
          <strong>{app.translator.trans('pakt21-beta.forum.profile.location_heading')}</strong>
          <span>{location}</span>
        </section>,
        68
      );
    }

    if (showProfileDetails && skills.length) {
      items.add(
        'pakt21Skills',
        <section className="Pakt21ProfileField">
          <strong>{app.translator.trans('pakt21-beta.forum.profile.skills_heading')}</strong>
          <span className="Pakt21SkillList">
            {skills.map((skill) => <span className="Pakt21Skill" key={skill}>{skill}</span>)}
          </span>
        </section>,
        67
      );
    }
    const actor = app.session.user;

    if (showProfileDetails && actor && (actor === user || actor.isAdmin())) {
      items.add(
        'pakt21EditProfile',
        <button
          type="button"
          className="Button Button--link Pakt21EditProfile"
          onclick={() => app.modal.show(ProfileDetailsModal, { user })}
        >
          {app.translator.trans('pakt21-beta.forum.profile_form.edit_button')}
        </button>,
        66
      );
    }
  });

  extend(PostUser.prototype, 'linkChildren', function (items, user) {
    const mark = memberMark(user, 'Pakt21MemberMark--post');

    if (mark) {
      items.add('pakt21MemberNumber', mark, 70);
    }
  });

  extend(PostUser.prototype, 'userViewItems', function (items, user) {
    if (!user.attribute('pakt21AnonymousIdentity')) return;

    items.remove('postUser-name');
    items.remove('postUser-badges');
    items.remove('postUser-card');
    items.add(
      'postUser-name',
      <h3 className="PostUser-name Pakt21AnonymousUser">
        {icon('fas fa-user-secret')} {app.translator.trans('pakt21-beta.forum.anonymous.public_name')}
      </h3>,
      100
    );
  });

  if (DiscussionListItem) {
    override(DiscussionListItem.prototype, 'authorAvatarView', function (original) {
      const discussion = this.attrs.discussion;
      const user = discussion.user();

      if (!discussion.attribute('pakt21IsAnonymous') || !user?.attribute('pakt21AnonymousIdentity')) {
        return original();
      }

      return (
        <span
          className="DiscussionListItem-author Pakt21AnonymousAvatar"
          title={app.translator.trans('pakt21-beta.forum.anonymous.public_name')}
        >
          {icon('fas fa-user-secret')}
        </span>
      );
    });

    extend(DiscussionListItem.prototype, 'infoItems', function (items) {
      const discussion = this.attrs.discussion;
      const type = discussion.attribute('pakt21ListingType');
      if (!type) return;

      items.add(
        'pakt21Listing',
        <span className={`Pakt21ListingBadge Pakt21ListingBadge--${discussion.attribute('pakt21ListingStatus')}`}>
          {listingTrans(`type_${type}`)} · {listingTrans(`status_${discussion.attribute('pakt21ListingStatus')}`)}
        </span>,
        80
      );
    });

    extend(DiscussionListItem.prototype, 'infoItems', function (items) {
      const discussion = this.attrs.discussion;
      const status = discussion.attribute('pakt21ArenaStatus');
      if (!status) return;

      items.add(
        'pakt21Arena',
        <span className={`Pakt21ArenaBadge Pakt21ArenaBadge--${status}`}>
          {arenaTrans(`status_${status}`)} · {arenaDateTime(discussion.attribute('pakt21ArenaScheduledAt'))}
        </span>,
        81
      );
    });
  }

  if (DiscussionHero) {
    extend(DiscussionHero.prototype, 'items', function (items) {
      const discussion = this.attrs.discussion;
      const tags = typeof discussion.tags === 'function' ? discussion.tags() : [];
      const tagSlug = tags[0]?.slug?.();
      const featuredImageUrl = discussion.attribute('pakt21FeaturedImageUrl');
      const heroImageUrl = !featuredImageUrl && tagSlug ? tagFallbackImageUrl(tagSlug, 800, 240) : null;

      if (heroImageUrl) {
        items.add(
          'pakt21HeroImage',
          <div className="Pakt21HeroImage">
            <img src={heroImageUrl} alt="" loading="lazy" />
          </div>,
          -5
        );
      }

      const type = discussion.attribute('pakt21ListingType');
      const arenaStatus = discussion.attribute('pakt21ArenaStatus');

      if (arenaStatus) {
        const scope = discussion.attribute('pakt21ArenaScope');
        const opponent = discussion.attribute('pakt21ArenaAcceptedByUsername')
          || discussion.attribute('pakt21ArenaTargetUsername');
        const acceptChallenge = () => {
          if (discussion.pakt21ArenaAccepting) return;
          discussion.pakt21ArenaAccepting = true;
          m.redraw();
          app.request({
            method: 'POST',
            url: pakt21ApiUrl(app.forum.attribute('apiUrl'), `/pakt21/arena/${discussion.id()}/accept`),
          }).then(() => app.store.find('discussions', discussion.id())).catch((error) => {
            app.alerts.show({ type: 'error' }, error?.response?.errors?.[0]?.detail || arenaTrans('accept_error'));
          }).finally(() => {
            discussion.pakt21ArenaAccepting = false;
            m.redraw();
          });
        };

        items.add(
          'pakt21ArenaDetails',
          <section className="Pakt21ArenaDetails">
            <div className="Pakt21ArenaDetails-heading">
              <strong>{arenaTrans('card_title')}</strong>
              <span className={`Pakt21ArenaBadge Pakt21ArenaBadge--${arenaStatus}`}>
                {arenaTrans(`status_${arenaStatus}`)}
              </span>
            </div>
            <dl>
              <div><dt>{arenaTrans('challenger')}</dt><dd>#{discussion.attribute('pakt21ArenaAuthorNumberSnapshot')}</dd></div>
              <div><dt>{arenaTrans('challenge_type')}</dt><dd>{arenaTrans(`scope_${scope}`)}</dd></div>
              {opponent ? <div><dt>{arenaTrans('opponent')}</dt><dd>{opponent}{discussion.attribute('pakt21ArenaAcceptedByNumberSnapshot') ? ` · #${discussion.attribute('pakt21ArenaAcceptedByNumberSnapshot')}` : ''}</dd></div> : null}
              <div><dt>{arenaTrans('scheduled_at')}</dt><dd>{arenaDateTime(discussion.attribute('pakt21ArenaScheduledAt'))}</dd></div>
            </dl>
            {discussion.attribute('pakt21ArenaRepliesLocked') ? <p>{arenaTrans('replies_locked')}</p> : <p>{arenaTrans('replies_open')}</p>}
            {discussion.attribute('pakt21ArenaCanAccept') ? (
              <Button className="Button Button--primary" icon="fas fa-handshake" loading={discussion.pakt21ArenaAccepting} onclick={acceptChallenge}>
                {arenaTrans('accept')}
              </Button>
            ) : null}
          </section>,
          -9
        );
      }

      if (!type) return;

      const location = discussion.attribute('pakt21ListingRemote')
        ? listingTrans('remote')
        : discussion.attribute('pakt21ListingLocation');
      const arrangement = discussion.attribute('pakt21ListingArrangement');
      const authorNumber = discussion.attribute('pakt21ListingAuthorNumberSnapshot');

      items.add(
        'pakt21ListingDetails',
        <section className="Pakt21ListingDetails">
          <div>
            <strong>{listingTrans(`type_${type}`)}</strong>
            <span className={`Pakt21ListingBadge Pakt21ListingBadge--${discussion.attribute('pakt21ListingStatus')}`}>
              {listingTrans(`status_${discussion.attribute('pakt21ListingStatus')}`)}
            </span>
          </div>
          <dl>
            <div><dt>{listingTrans('location')}</dt><dd>{location}</dd></div>
            <div><dt>{listingTrans('arrangement')}</dt><dd>{listingTrans(`arrangement_${arrangement}`)}</dd></div>
            {discussion.attribute('pakt21ListingPrice') ? <div><dt>{listingTrans('price')}</dt><dd>{discussion.attribute('pakt21ListingPrice')}</dd></div> : null}
            {discussion.attribute('pakt21ListingAvailability') ? <div><dt>{listingTrans('availability')}</dt><dd>{discussion.attribute('pakt21ListingAvailability')}</dd></div> : null}
            <div>
              <dt>PAKT21 ID</dt>
              <dd>{authorNumber ? `#${authorNumber}` : listingTrans('author_id_unassigned')}</dd>
            </div>
          </dl>
          <p>{listingTrans('no_payment_notice')}</p>
        </section>,
        -10
      );
    });
  }

  if (CommentPost) {
    extend(CommentPost.prototype, 'bodyItems', function (items) {
      const post = this.attrs.post;

      if (Number(post.number()) !== 1) return;

      const discussion = post.discussion();
      const imageUrl = discussion?.attribute('pakt21FeaturedImageUrl');

      if (!imageUrl || this.isEditing()) return;

      items.add(
        'pakt21FeaturedImage',
        <figure className="Pakt21PostFeaturedImage">
          <img src={imageUrl} alt="" loading="eager" />
        </figure>,
        110
      );
    });
  }

  extend(DiscussionControls, 'userControls', function (items, discussion) {
    if (discussion.attribute('pakt21CanFulfillListing')) {
      items.add('pakt21ListingFulfill', <Button icon="fas fa-check" onclick={() => app.modal.show(ListingStatusModal, { discussion, status: 'fulfilled' })}>{listingTrans('action_fulfilled')}</Button>, 80);
    }
    if (discussion.attribute('pakt21CanCloseListing')) {
      items.add('pakt21ListingClose', <Button icon="fas fa-times" onclick={() => app.modal.show(ListingStatusModal, { discussion, status: 'closed' })}>{listingTrans('action_closed')}</Button>, 79);
    }
  });

  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    if (discussion.attribute('pakt21CanHideListing')) {
      items.add('pakt21ListingHide', <Button icon="fas fa-eye-slash" onclick={() => app.modal.show(ListingStatusModal, { discussion, status: 'hidden', requireReason: true })}>{listingTrans('action_hidden')}</Button>, 82);
    }
    if (discussion.attribute('pakt21CanRestoreListing')) {
      items.add('pakt21ListingRestore', <Button icon="fas fa-eye" onclick={() => app.modal.show(ListingStatusModal, { discussion, status: 'published', requireReason: true })}>{listingTrans('action_published')}</Button>, 81);
    }

    if (discussion.attribute('pakt21CanReject')) {
      items.add(
        'pakt21Reject',
        <Button
          icon="fas fa-ban"
          onclick={() =>
            app.modal.show(ModerationDecisionModal, {
              discussion,
              action: 'reject',
            })
          }
        >
          {app.translator.trans('pakt21-beta.forum.moderation.reject_button')}
        </Button>,
        80
      );
    }

    if (discussion.attribute('pakt21CanRestore')) {
      items.add(
        'pakt21Restore',
        <Button
          icon="fas fa-undo"
          onclick={() =>
            app.modal.show(ModerationDecisionModal, {
              discussion,
              action: 'restore',
            })
          }
        >
          {app.translator.trans('pakt21-beta.forum.moderation.restore_button')}
        </Button>,
        79
      );
    }
  });
});
